// adapted from https://github.com/fboeller/microfrontends-with-angular/blob/recording/projects/train-platform/src/micro-frontends/bookings-host.component.ts
import { CommonModule } from '@angular/common';
import {
  CUSTOM_ELEMENTS_SCHEMA,
  Component,
  inject,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { NavigationEnd, NavigationExtras, Router } from '@angular/router';
import { distinctUntilChanged, Observable, Subject } from 'rxjs';
import { filter, map, startWith, takeUntil } from 'rxjs/operators';

import {
  AuthInputEvent,
  AuthOutputEvent,
} from '@digital-platform/aura-authentication/domain';
import { AuraAuthService } from '@digital-platform/aura-authentication/services';
import { AddressLookupPrepop } from '@digital-platform/rs-addresses/domain';
import { AddressLookupPrepopService } from '@digital-platform/rs-addresses/services';
import { User, UserFromClassic } from '@digital-platform/rs-auth/domain';
import { IdentityService } from '@digital-platform/rs-auth/services';
import { PersistantCartService } from '@digital-platform/rs-cart/services';
import {
  ClassicCartRepository,
  LegacyCart,
} from '@digital-platform/rs-classic/classic-state';
import { ShowClassicContentService } from '@digital-platform/rs-classic/services';
import { RsClassicUiModule } from '@digital-platform/rs-classic/ui';
import {
  NotificationItem,
  NotificationsService,
} from '@digital-platform/rs-notifications/services';
import {
  PrerenderService,
  SpinnerService,
} from '@digital-platform/rs-shared/services';
import { CartRepository } from '@digital-platform/rs-shop/shop-state';
import { SupportNumberService } from '@digital-platform/rs-support/services';

export interface RouterEventWithExtras {
  url: string;
  extras?: NavigationExtras;
}

// disabling view encapsulation is required to allow css fixes to work
@Component({
  selector: 'digital-platform-classic-host',
  standalone: true,
  imports: [CommonModule, RsClassicUiModule],
  providers: [],
  styleUrls: ['./classic-host.component.scss'],
  templateUrl: './classic-host.component.html',
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class ClassicHostComponent implements OnDestroy, OnInit {
  public routeForV1?: string;
  public loginState?: string;
  public authInputEvent?: string; // @AURA
  public cartRefresh = false;
  public supportNumber?: string;
  public openChat = false;
  public showContent$?: Observable<boolean>;

  private notificationsService = inject(NotificationsService);
  private cartRepository = inject(CartRepository);
  private classicCartRepository = inject(ClassicCartRepository);
  private persistantCartService = inject(PersistantCartService);
  private identityService = inject(IdentityService);
  private router = inject(Router);
  private spinner = inject(SpinnerService);
  private showClassicContentService = inject(ShowClassicContentService);
  private supportNumberService: SupportNumberService =
    inject(SupportNumberService);
  private addressLookupPrepopService = inject(AddressLookupPrepopService);
  private prerenderService = inject(PrerenderService);
  private auraAuthService = inject(AuraAuthService); //@AURA
  private previousIdentity?: string;
  private destroyed$ = new Subject<void>();
  private identityForClassic$ = this.getIdentityStreamForClassic();

  ngOnInit(): void {
    this.showContent$ = this.showClassicContentService.showClassicContent$;
    this.showContent$
      .pipe(takeUntil(this.destroyed$))
      .subscribe((showClassicContent) => {
        // eslint-disable-next-line no-restricted-syntax
        console.debug(showClassicContent ? 'Showing V1 UI' : 'Showing V2 UI');
      });

    this.showClassicContentService.openChat$.subscribe((): void => {
      this.openChat = !this.openChat;
    });

    // Initialize service that handles persistant cart and canadian modal
    this.persistantCartService.initialize();

    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        startWith(this.router),
        takeUntil(this.destroyed$)
      )
      .subscribe(() => {
        const extras = this.router.getCurrentNavigation()?.extras;

        const urlWithExtras = JSON.stringify({
          url: this.router.url,
          ...(extras && {
            extras,
          }),
        });

        // sanity check
        if (this.routeForV1 !== urlWithExtras && !this.v2RouteHandled()) {
          this.routeForV1 = urlWithExtras;
        }
      });

    this.identityForClassic$
      .pipe(takeUntil(this.destroyed$))
      .subscribe((identity) => {
        let isRsIdentity = false;

        try {
          const parsedIdentity = JSON.parse(identity);
          isRsIdentity =
            'isAuthenticated' in parsedIdentity && 'loaded' in parsedIdentity;

          if (!isRsIdentity) {
            this.previousIdentity = identity;
            return;
          }
        } catch (e) {}

        if (identity && identity !== this.previousIdentity) {
          // NOTE: This sets the loginState as a watched @Input of the <classic-content-wrapper> component
          if (this.loginState !== identity) {
            this.loginState = identity;
          }
        }
        this.previousIdentity = identity;
      });

    // TODO: V1: We will probably need this if we still have any shop in V1
    this.cartRepository.cartCleared$
      .pipe(takeUntil(this.destroyed$))
      .subscribe(() => {
        this.cartRefresh = !this.cartRefresh;
      });

    this.supportNumberService.supportNumber$
      .pipe(takeUntil(this.destroyed$), distinctUntilChanged())
      .subscribe(({ supportNumber }) => {
        this.supportNumber = supportNumber;
      });

    // Forward auth events from aura auth service to classic
    this.auraAuthService.authEvents$
      .pipe(takeUntil(this.destroyed$))
      .subscribe((e: AuthInputEvent) => {
        try {
          this.authInputEvent = JSON.stringify(e);
        } catch (authError) {
          console.error('Error stringifying auth event for classic', authError);
        }
      });

    this.auraAuthService.notifyV1Ready();
    // Kicks off the auth0 session storage and mock login flow
    // this.auraAuthService.initializeLocalSession();
  }

  /**
   * @AURA - This is the event handler for the auth output event from the classic content
   * @param event
   */
  public handleAuthOutputEvent(event: { detail?: AuthOutputEvent }): void {
    if (event.detail?.type === 'Logout') {
      this.identityService.repo.reset(); // Clean out identity repo
    }

    this.auraAuthService.handleAuthOutputEvent(event.detail);
  }

  public handleLoading(event: CustomEvent<boolean>) {
    if (event.detail) {
      this.spinner.startLoading('classic');
      return;
    }

    this.spinner.stopLoading('classic');
  }

  public handleSupportNumberChange(event: { detail?: string }) {
    this.supportNumberService.setSupportNumber(event.detail);
  }

  public handleAddressLookupPrepopChange(event: {
    detail?: AddressLookupPrepop | undefined;
  }) {
    this.addressLookupPrepopService.setAddressLookupPrepop(event.detail);
  }

  public ngOnDestroy(): void {
    this.spinner.stopLoading('classic');
    this.destroyed$.next();
    this.destroyed$.complete();
  }

  public navigateToUrl(event: RouterEventWithExtras | undefined): void {
    if (event?.url && event.url.startsWith('/')) {
      // sanity check to avoid infinite routing loops
      if (event.url !== this.router.url) {
        // eslint-disable-next-line no-restricted-syntax
        console.debug('V2: Received Route Event from Classic', {
          event,
        });
        this.router.navigateByUrl(event.url, {
          ...event.extras,
        });
      }
    } else {
      console.warn(`navigateToUrl() received an invalid router event.`, event);
    }
  }

  public handleloginChanged(event: { detail?: User | undefined }) {
    this.authenticate(event.detail);
  }

  public handleRouteChange(event: { detail?: RouterEventWithExtras }) {
    this.navigateToUrl(event.detail);
  }

  public handleNotificationStateChanged(event: {
    detail?: NotificationItem[];
  }) {
    if (!event.detail || event.detail.length === 0) {
      this.notificationsService.reset();
    } else {
      // Save the notifications to the global v2 state
      this.notificationsService.set(event.detail);
    }
  }

  // TODO: V1: handles cart changes coming from V1 and propogated to V2
  public handleCartChange(event: { detail?: LegacyCart }) {
    if (!event.detail || event.detail.cartReset) {
      return;
    }

    // We dont need to pass the cart object just make V2 aware that the cart has changed
    this.classicCartRepository.triggerLegacyV1CartChanged();
  }

  public handlePrerenderReady(event: { detail?: boolean }) {
    this.prerenderService.setReady(event.detail || false);
  }

  // Auth related code
  private authenticate(data?: User | undefined): void {
    if (data) {
      try {
        const identity = UserFromClassic.parse(data);

        this.identityService.login(identity);
      } catch (err) {
        console.error('Got error processing login state from classic', {
          data,
          err,
        });
      }
      return;
    }
  }

  private getIdentityStreamForClassic(): Observable<string> {
    return this.identityService.state$.pipe(
      map((user) => {
        if (user.profile?.sub) {
          return user;
        }

        return;
      }),
      map((identity) => JSON.stringify(identity) || 'undefined')
    );
  }

  private v2RouteHandled(): boolean {
    let route = this.router.routerState.root;
    while (route.firstChild) {
      route = route.firstChild;
    }

    return !!route.snapshot.data['v2RouteHandled'];
  }
}
